import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { toast } from "react-toastify";
import { FiX, FiUserPlus } from "react-icons/fi";
import { tokens, surface, hairline } from "../../../styles/designTokens";
import { mono } from "../styles";

const overlayFade = keyframes`
  from { opacity: 0; } to { opacity: 1; }
`;
const modalIn = keyframes`
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(10px) saturate(140%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 2em;
  animation: ${overlayFade} ${tokens.durBase} ${tokens.easeOut};
`;

const Box = styled.div`
  background: ${({ theme }) => surface(theme, 0)};
  border: 1px solid ${({ theme }) => hairline(theme)};
  border-radius: ${tokens.radiusXl};
  width: 100%;
  max-width: 440px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
  animation: ${modalIn} ${tokens.durBase} ${tokens.easeOut};
  overflow: hidden;

  .box-header {
    padding: 18px 22px;
    border-bottom: 1px solid ${({ theme }) => hairline(theme)};
    display: flex;
    align-items: center;
    justify-content: space-between;

    h3 {
      margin: 0;
      font-size: 1.05em;
      font-weight: 600;
      color: ${({ theme }) => theme.text};
      letter-spacing: -0.01em;
    }

    button {
      width: 30px;
      height: 30px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      opacity: 0.65;
      cursor: pointer;

      &:hover { opacity: 1; }
    }
  }

  .box-body {
    padding: 20px 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
  }

  .box-footer {
    padding: 14px 22px 18px;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
  }

  .field {
    display: flex;
    flex-direction: column;
    gap: 6px;

    label {
      ${mono};
      font-size: 0.62em;
      font-weight: 500;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: ${({ theme }) => theme.text};
      opacity: 0.5;
    }

    input {
      padding: 10px 12px;
      background: ${({ theme }) => surface(theme, 1)};
      border: 1px solid ${({ theme }) => hairline(theme)};
      border-radius: ${tokens.radiusMd};
      color: ${({ theme }) => theme.text};
      font-family: ${tokens.fontSans};
      font-size: 0.85em;
      outline: none;

      &:focus { border-color: ${tokens.brandRing}; }
    }

    .tags-select {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;

      button {
        padding: 6px 12px;
        border-radius: ${tokens.radiusPill};
        border: 1px solid ${({ theme }) => hairline(theme)};
        background: transparent;
        color: ${({ theme }) => theme.text};
        opacity: 0.6;
        font-size: 0.74em;
        cursor: pointer;
        transition: all ${tokens.durBase} ${tokens.easeOut};

        &.active {
          background: ${tokens.brandSoft};
          border-color: ${tokens.brandRing};
          color: ${tokens.brand};
          opacity: 1;
        }
      }
    }
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: ${tokens.brand};
    color: white;
    border: 1px solid ${tokens.brand};
    border-radius: ${tokens.radiusMd};
    font-size: 0.85em;
    font-weight: 500;
    cursor: pointer;
    transition: all ${tokens.durBase} ${tokens.easeOut};

    &:hover:not(:disabled) { background: ${tokens.brandHover}; transform: translateY(-1px); }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  .btn-secondary {
    padding: 10px 20px;
    background: transparent;
    color: ${({ theme }) => theme.text};
    border: 1px solid ${({ theme }) => hairline(theme)};
    border-radius: ${tokens.radiusMd};
    font-size: 0.85em;
    font-weight: 500;
    cursor: pointer;

    &:hover { background: ${({ theme }) => surface(theme, 1)}; }
  }
`;

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AddContactModal({ isOpen, onClose, lists, onAddContact }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedLists, setSelectedLists] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const toggleList = (id) => {
    setSelectedLists((prev) =>
      prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id]
    );
  };

  const reset = () => {
    setName("");
    setEmail("");
    setSelectedLists([]);
    setSubmitting(false);
  };

  const handleSubmit = async () => {
    if (!name.trim() || !emailRegex.test(email.trim())) {
      toast.error("Preencha um nome e um email válido");
      return;
    }
    setSubmitting(true);
    try {
      await onAddContact({ name: name.trim(), email: email.trim(), listIds: selectedLists });
      toast.success("Contato adicionado com sucesso");
      reset();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao adicionar contato");
      setSubmitting(false);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Box onClick={(e) => e.stopPropagation()}>
        <div className="box-header">
          <h3>Adicionar Contato</h3>
          <button onClick={onClose}><FiX /></button>
        </div>

        <div className="box-body">
          <div className="field">
            <label>Nome</label>
            <input
              type="text"
              placeholder="Nome do contato"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="field">
            <label>Email</label>
            <input
              type="email"
              placeholder="email@exemplo.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="field">
            <label>Listas</label>
            <div className="tags-select">
              {lists.length === 0 && (
                <span style={{ opacity: 0.5, fontSize: "0.78em" }}>Nenhuma lista criada ainda</span>
              )}
              {lists.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  className={selectedLists.includes(l.id) ? "active" : ""}
                  onClick={() => toggleList(l.id)}
                >
                  {l.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="box-footer">
          <button className="btn-secondary" onClick={onClose} disabled={submitting}>Cancelar</button>
          <button className="btn-primary" onClick={handleSubmit} disabled={submitting}>
            <FiUserPlus /> {submitting ? "Adicionando..." : "Adicionar"}
          </button>
        </div>
      </Box>
    </Overlay>
  );
}
